import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const HERO = "/dz-laptop-scores.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/public/branding").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const submit = async (e, override) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const u = await login(override?.email || email, override?.password || password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      navigate(u.role === "client" ? "/portal" : u.role === "diy" ? "/diy" : "/admin");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            {brand?.logo_url
              ? <img src={brand.logo_url} alt={brand.company_name} className="h-12 w-12 rounded object-contain" />
              : <ShieldCheck className="h-9 w-9 text-[#0B2545]" />}
            <div>
              <div className="font-heading font-extrabold text-2xl text-[#0B2545] tracking-tight" data-testid="login-brand-name">{brand?.company_name || "D&Z Management"}</div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{brand?.tagline || "Credit Repair Operations Platform"}</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1">Sign in</h1>
          <p className="text-sm text-slate-500 mb-6">Staff workspace and client portal use the same secure login.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" data-testid="login-email-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password-input" className="mt-1" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-btn"
              className="w-full bg-[#0B2545] hover:bg-[#134074] text-white font-semibold">
              {busy ? "Signing in…" : "Sign in securely"}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-4">
            New client? <Link to="/register" className="text-blue-700 font-medium hover:underline" data-testid="go-register-link">Create your account</Link>
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Run a credit repair business? <Link to="/agency-signup" className="text-blue-700 font-medium hover:underline" data-testid="go-agency-signup-link">Get a white-label workspace</Link>
          </p>
          <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">Demo one-click sign in</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="role-switcher-staff" disabled={busy}
                onClick={(e) => submit(e, { email: "4vt9hz4j62@privaterelay.appleid.com", password: "Admin2026!Secure" })}>
                Staff OS (Admin)
              </Button>
              <Button size="sm" variant="outline" data-testid="role-switcher-manager" disabled={busy}
                onClick={(e) => submit(e, { email: "manager@credvantage.com", password: "Manager2026!" })}>
                Case Manager
              </Button>
              <Button size="sm" variant="outline" data-testid="role-switcher-client" disabled={busy}
                onClick={(e) => submit(e, { email: "jasmine.carter@example.com", password: "Client2026!" })}>
                Client Portal
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Credit repair consultation" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B2545]/70" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-heading text-4xl font-extrabold leading-tight mb-3">Welcome to Your Credit Comeback —<br />better credit starts here.</h2>
          <p className="text-sky-100 max-w-md text-sm leading-relaxed">
            FCRA-compliant dispute pipelines, AI-assisted report review, 30-day statutory timers, and certified USPS mailing — all in one operations platform.
          </p>
        </div>
      </div>
    </div>
  );
}
